import { AxiosResponse } from 'axios'
import {
  CustomerInterfaceViewModel,
  Guid,
  ApiResult,
  EmptyApiResult,
  CustomerCertificateSigningRequestViewModel,
  ApiConnectorConfigurationRequestViewModel,
  ImportConfigurationViewModel,
  OptionalWithAlternateValue,
} from '../api'
import { useApiClient } from './useApiClient'
import { CustomerInterface } from 'contexts/CurrentCustomerContext'
import { CsrResponse } from './connectorApi'

type InterfaceConfiguration = {
  [key: string]: string
}

export const useConnectorApi = () => {
  const apiClient = useApiClient()

  const editConnector = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
    values: CustomerInterface,
  ) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerInterfaceViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}`,
      method: 'put',
      data: values,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  const addConnector = (
    customerAgencyId: Guid,
    values: CustomerInterfaceViewModel,
  ) => {
    const formData = new FormData()
    Object.keys(values).forEach((key: string) => {
      formData.append(key, (values as any)[key])
    })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerInterfaceViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const deleteConnector = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
  ) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const createCertificateFromCsr = (
    values: CustomerCertificateSigningRequestViewModel,
  ) => {
    return apiClient.request<void, AxiosResponse<CsrResponse>>({
      url: 'api/customerInterfaces/certificate',
      method: 'post',
      data: values,
      withCredentials: true,
    })
  }

  const revokeCertificate = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
  ) => {
    return apiClient.post<ApiResult<CustomerInterfaceViewModel>>(
      `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}/certificate/revoke`,
      undefined,
      {
        withCredentials: true,
      },
    )
  }

  const reinstateCertificate = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
  ) => {
    return apiClient.post<ApiResult<CustomerInterfaceViewModel>>(
      `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}/certificate/reinstate`,
      undefined,
      {
        withCredentials: true,
      },
    )
  }

  const configureConnector = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
    values: ApiConnectorConfigurationRequestViewModel,
  ) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<InterfaceConfiguration>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}/configure`,
      method: 'post',
      data: values,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  const getConnectorImportConfiguration = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
  ) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<ImportConfigurationViewModel | null>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}/settings/import`,
      method: 'get',
      withCredentials: true,
    })
  }

  const updateConnectorImportConfiguration = (
    customerAgencyId: Guid,
    customerInterfaceId: Guid,
    values: ImportConfigurationViewModel | null,
  ) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/customerAgencies/${customerAgencyId}/customerInterfaces/${customerInterfaceId}/settings/import`,
      method: 'put',
      data: values,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  return {
    editConnector,
    addConnector,
    deleteConnector,
    createCertificateFromCsr,
    revokeCertificate,
    reinstateCertificate,
    configureConnector,
    getConnectorImportConfiguration,
    updateConnectorImportConfiguration,
  }
}
